import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import * as React from "react";



/**
 *? Este es un componente que no se ha llegado a poner en uso, ya que se optó por usar el componente de calendario de react-day-picker.
 *? Cuando estemos listos para hacer el cambio, esto quita una dependencia grande y un monton de pegamento para que react-day-picker funcione con Temporal.
 */





type NullishDate = Temporal.PlainDate | undefined;
interface SingleProps {
	readonly selectedDate: NullishDate;
	readonly setSelectedDate: Setter<NullishDate>;
}
interface RangeProps {
	readonly selectedRange: Readonly<[NullishDate, NullishDate]>;
	readonly setSelectedRange: Setter<Readonly<[NullishDate, NullishDate]>>;
}

export interface CalendarProps {
	/**
	 * In the year view, the number of years to display at once.
	 * @default 12
	 */
	readonly yearRange?: number;
	readonly numberOfMonths?: number;
}
export function Calendar(props: CalendarProps & (SingleProps | RangeProps)) {
	const WEEK_STARTS_ON = 1;

	const [shownMonth, setShownMonth] = React.useState(
		Temporal.Now.plainDateISO().toPlainYearMonth(),
	);
	const [showYearView, setShowYearView] = React.useState(false);

	if (showYearView) {
		return (
			<div className="grid p-3">
				<div className="relative flex gap-5">
					<YearsView
						shownMonth={shownMonth}
						setShownMonth={setShownMonth}
						setShowYearView={setShowYearView}
					/>
				</div>
			</div>
		);
	}

	const shownMonths = new Array(props.numberOfMonths ?? 1).fill(0).map((_, i) => shownMonth.add({ months: i }));

	if ("selectedRange" in props) {
		return (
			<CalendarWrapper setShownMonth={setShownMonth}>
				{shownMonths.map((month) => (
					<DaysGridRange
						key={month.toString()}
						selectedRange={props.selectedRange}
						setSelectedRange={props.setSelectedRange}
						setShowYearView={setShowYearView}
						shownMonth={month}
						weekStartsOn={WEEK_STARTS_ON}
					/>
				))}
			</CalendarWrapper>
		);
	} else {
		return (
			<CalendarWrapper setShownMonth={setShownMonth}>
				<DaysGridSingle
					selectedDate={props.selectedDate}
					setSelectedDate={props.setSelectedDate}
					setShowYearView={setShowYearView}
					shownMonth={shownMonth}
					weekStartsOn={WEEK_STARTS_ON}
				/>
			</CalendarWrapper>
		);
	}
}

function CalendarWrapper(props: { readonly children: React.ReactNode; readonly setShownMonth: Setter<Temporal.PlainYearMonth>; }) {
	const onNextClick = () => props.setShownMonth((month) => month.add({ months: 1 }));
	const onPrevClick = () => props.setShownMonth((month) => month.subtract({ months: 1 }));

	return (
		<div className="grid p-3">
			<div className="relative flex gap-5">
				<NavButtons onNextClick={onNextClick} onPrevClick={onPrevClick} />
				{props.children}
			</div>
		</div>);
}

function NavButtons(props: { readonly onNextClick: () => void; readonly onPrevClick: () => void; }) {
	const style = { height: "28px", width: "28px", padding: "0", position: "absolute", top: 0, } as const;

	return (
		<nav style={{ display: "contents" }}>
			<Button
				type="button"
				variant="outline"
				title="Previous month"
				style={{ ...style, left: 0 }}
				onClick={props.onPrevClick}
			>
				<svg
					focusable="false"
					aria-hidden="true"
					viewBox="0 0 24 24"
					style={{ height: "100%" }}
				>
					<path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"></path>
				</svg>
			</Button>

			<Button
				type="button"
				variant="outline"
				title="Next month"
				style={{ ...style, right: 0 }}
				onClick={props.onNextClick}
			>
				<svg
					focusable="false"
					aria-hidden="true"
					viewBox="0 0 24 24"
					style={{ height: "100%" }}
				>
					<path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"></path>
				</svg>
			</Button>
		</nav>
	);
}

function MonthHeader(props: { readonly month: Temporal.PlainYearMonth; readonly setShowYearView: Setter<boolean>; }) {
	return (
		<div className="monthHeader flex justify-center items-center p-0 mx-10">
			<Button variant="ghost" role="presentation" aria-live="polite" onClick={() => props.setShowYearView(true)} className="cursor-pointer h-auto py-0 w-full px-4">
				{props.month.toLocaleString(undefined, {
					calendar: props.month.calendarId,
					month: "long",
				})}{" "}
				{props.month.toLocaleString(undefined, {
					calendar: props.month.calendarId,
					year: "numeric",
				})}
			</Button>
		</div>
	);
}

function WeekdayHeader(props: { readonly weekStartsOn: number; }) {
	const weekdays = [];
	for (let i = 0; i < 7; i++) {
		const dayOfWeek = (props.weekStartsOn + i) % 7 || 7;
		const plainDay = Temporal.PlainDate.from({ year: 2024, month: 1, day: dayOfWeek });
		weekdays.push(plainDay.toLocaleString(undefined, { weekday: "short" }));
	}

	return (
		<tr className="weekContainer">
			{weekdays.map((weekday) => (
				<th key={weekday} className="text-sm font-medium text-muted-foreground font-normal">
					{weekday}
				</th>
			))}
		</tr>
	);
}

function YearsView(props: { readonly shownMonth: Temporal.PlainYearMonth; readonly setShownMonth: Setter<Temporal.PlainYearMonth>; readonly setShowYearView: Setter<boolean>; }) {
	const [extraYears, setExtraYears] = React.useState(0);
	const handleSelectYear = (year: number) => {
		const newMonth = props.shownMonth.with({ year });
		props.setShownMonth(newMonth);
		props.setShowYearView(false);
	};
	const selectedYear = props.shownMonth.year;
	const centerYear = selectedYear + extraYears;

	return (
		<div className="w-full">
			<NavButtons onNextClick={() => { setExtraYears(extraYears + 4); }} onPrevClick={() => { setExtraYears(extraYears - 4); }} />
			<div className="relative mx-10 flex h-7 items-center justify-center">
				<button data-slot="button" className="inline-flex items-center justify-center whitespace-nowrap transition-all cursor-pointer disabled:pointer-events-none disabled:opacity-50 [&amp;_svg]:pointer-events-none [&amp;_svg:not([class*='size-'])]:size-4 shrink-0 [&amp;_svg]:shrink-0 outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive hover:bg-accent hover:text-accent-foreground rounded-md gap-1.5 px-3 has-[&gt;svg]:px-2.5 h-7 w-full truncate text-sm font-medium" onClick={() => props.setShowYearView(false)}>
					{centerYear - 5} - {centerYear + 6}
				</button>
			</div>
			<div className="grid grid-cols-4 gap-y-2 mx-auto mt-4" role="grid" aria-multiselectable="false">
				<YearButton year={centerYear - 5} isSelected={selectedYear == centerYear - 5} onClick={handleSelectYear} />
				<YearButton year={centerYear - 4} isSelected={selectedYear == centerYear - 4} onClick={handleSelectYear} />
				<YearButton year={centerYear - 3} isSelected={selectedYear == centerYear - 3} onClick={handleSelectYear} />
				<YearButton year={centerYear - 2} isSelected={selectedYear == centerYear - 2} onClick={handleSelectYear} />
				<YearButton year={centerYear - 1} isSelected={selectedYear == centerYear - 1} onClick={handleSelectYear} />
				<YearButton year={centerYear + 0} isSelected={selectedYear == centerYear + 0} onClick={handleSelectYear} />
				<YearButton year={centerYear + 1} isSelected={selectedYear == centerYear + 1} onClick={handleSelectYear} />
				<YearButton year={centerYear + 2} isSelected={selectedYear == centerYear + 2} onClick={handleSelectYear} />
				<YearButton year={centerYear + 3} isSelected={selectedYear == centerYear + 3} onClick={handleSelectYear} />
				<YearButton year={centerYear + 4} isSelected={selectedYear == centerYear + 4} onClick={handleSelectYear} />
				<YearButton year={centerYear + 5} isSelected={selectedYear == centerYear + 5} onClick={handleSelectYear} />
				<YearButton year={centerYear + 6} isSelected={selectedYear == centerYear + 6} onClick={handleSelectYear} />
			</div>
		</div>

	);
}
function YearButton(props: { readonly year: number; readonly isSelected: boolean; readonly onClick: (year: number) => void; }) {
	const selectedClass = props.isSelected ? "bg-accent text-accent-foreground" : "";
	const className = cn(
		"h-7 w-full text-sm font-normal text-foreground",
		selectedClass
	);
	return (
		<Button className={className} variant="ghost" onClick={() => props.onClick(props.year)}>
			{props.year}
		</Button>

	);
}


interface GridProps {
	readonly shownMonth: Temporal.PlainYearMonth;
	readonly weekStartsOn: number;
	readonly setShowYearView: Setter<boolean>;
}
function DaysGridSingle(props: SingleProps & GridProps) {
	let currentDate = props.shownMonth.toPlainDate({ day: 1 });
	if (currentDate.dayOfWeek !== props.weekStartsOn) {
		const daysLastMonth = currentDate.dayOfWeek - props.weekStartsOn;
		currentDate = currentDate.subtract({ days: daysLastMonth });
	}

	const daysRows = [];
	while (daysRows.length < 6) {
		const row = [];
		for (let i = 0; i < currentDate.daysInWeek; i++) {
			row.push(currentDate);
			currentDate = currentDate.add({ days: 1 });
		}
		daysRows.push(row);
	}

	const { selectedDate, setSelectedDate } = props;
	const isSelected = (date: Temporal.PlainDate) =>
		selectedDate && date.equals(selectedDate) ? "selected" : "none";
	const handleDayClick = (date: Temporal.PlainDate) => {
		return () => {
			if (selectedDate?.equals(date)) {
				setSelectedDate(undefined);
			} else {
				setSelectedDate(date);
			}
		};
	};

	return (
		<div>
			<MonthHeader month={props.shownMonth} setShowYearView={props.setShowYearView} />
			<table className="monthContainer">
				{daysRows.map((row) => (
					<tr key={row.toString()} className="weekContainer">
						{row.map((date) => (
							<DayCell
								key={date.toJSON()}
								date={date}
								selection={isSelected(date)}
								handleClick={handleDayClick(date)}
								{...props}
							/>
						))}
					</tr>
				))}
			</table>
		</div>
	);
}
function DaysGridRange(props: RangeProps & GridProps) {
	const daysRows = getGridDays(props.shownMonth, props.weekStartsOn);

	const { selectedRange, setSelectedRange } = props;
	const isSelected = (date: Temporal.PlainDate) => {
		if (selectedRange[0] == undefined && selectedRange[1] == undefined) {
			return "none";
		}
		if (selectedRange[0] && selectedRange[1] && selectedRange[0].equals(date) && selectedRange[1].equals(date)) {
			return "selected";
		}
		if (selectedRange[0] && date.equals(selectedRange[0])) {
			return "start";
		}
		if (selectedRange[1] && date.equals(selectedRange[1])) {
			return "end";
		}
		if (selectedRange[0] == undefined || selectedRange[1] == undefined) {
			return "none";
		}
		if (
			date.since(selectedRange[0]).sign >= 0 &&
			date.since(selectedRange[1]).sign <= 0
		) {
			return "inside-selection";
		}
		return "none";
	};
	const handleDayClick = (date: Temporal.PlainDate) => {
		return () => {
			// No start date selected, set as start date and clear end date
			if (selectedRange[0] == undefined) {
				setSelectedRange([date, selectedRange[1]]);
				return;
			}

			// Previous to start date selected, set as start date
			if (selectedRange[0].since(date).sign == 1) {
				setSelectedRange([date, undefined]);
				return;
			}

			// No end date selected, set as end date
			if (selectedRange[1] == undefined) {
				setSelectedRange([selectedRange[0], date]);
				return;
			}

			// Later than end date selected, set as end date and clear start date
			if (selectedRange[1].since(date).sign == -1) {
				setSelectedRange([selectedRange[0], date]);
				return;
			}

			// Clicked on the start date, remove that date
			if (selectedRange[0].since(date).sign == 0) {
				setSelectedRange([undefined, selectedRange[1]]);
				return;
			}

			// Clicked on the end date, remove that date
			if (selectedRange[1].since(date).sign == 0) {
				setSelectedRange([selectedRange[0], undefined]);
				return;
			}

			//So, you clicked inbetween the start and end dates. So ill set the start date and clear the end date.
			setSelectedRange([date, undefined]);

		};
	};

	return (
		<div>
			<MonthHeader month={props.shownMonth} setShowYearView={props.setShowYearView} />
			<table className="monthContainer mt-4 mx-1">
				<WeekdayHeader weekStartsOn={props.weekStartsOn} />
				{daysRows.map((row) => (
					<tr key={row.toString()} className="weekContainer">
						{row.map((date) => (
							<DayCell
								date={date}
								selection={isSelected(date)}
								handleClick={handleDayClick(date)}
								{...props}
								key={date.toJSON()}
							/>
						))}
					</tr>
				))}
			</table>
		</div>
	);
}
function getGridDays(shownMonth: Temporal.PlainYearMonth, weekStartsOn: number) {
	let currentDate = shownMonth.toPlainDate({ day: 1 });
	if (currentDate.dayOfWeek !== weekStartsOn) {
		const daysLastMonth = currentDate.dayOfWeek - weekStartsOn;
		currentDate = currentDate.subtract({ days: daysLastMonth });
	}

	const daysRows = [];
	while (daysRows.length < 6) {
		const row = [];
		for (let i = 0; i < currentDate.daysInWeek; i++) {
			row.push(currentDate);
			currentDate = currentDate.add({ days: 1 });
		}
		daysRows.push(row);
	}
	return daysRows;
}


interface DayProps {
	readonly date: Temporal.PlainDate;
	readonly shownMonth: Temporal.PlainYearMonth;

	readonly selection: "selected" | "start" | "end" | "inside-selection" | "none";
	readonly handleClick: () => void;
}
function DayCell(props: DayProps) {
	const { date, shownMonth, selection } = props;

	const joinClassNames = (classNames: Array<string | false | undefined>) =>
		classNames.filter((className) => className != undefined).join(" ");

	const now = Temporal.Now.plainDateISO();

	return (
		<td>
			{date.month == shownMonth.month && (
				<Button
					variant="ghost"
					style={{ height: "32px", width: "32px", margin: "3px 0", fontSize: "14px" }}
					onClick={props.handleClick}
					className={joinClassNames([
						"dayContainer transition-none",
						date.month != shownMonth.month && "outsideCurrentMonth",
						date.toPlainYearMonth().daysInMonth === date.day && "endOfMonth",
						date.day === 1 && "startOfMonth",
						date.equals(now) && "today",

						selection == "selected" && "selected bg-primary text-primary-foreground",
						selection == "start" && "selected bg-primary text-primary-foreground rounded-e-none",
						selection == "end" && "selected bg-primary text-primary-foreground rounded-s-none",
						selection == "inside-selection" && "insideSelection bg-secondary text-secondary-foreground rounded-none",
					])}
				>
					{date.day}
				</Button>
			)}
		</td>
	);
}
